import { useState } from 'react';

import moment from 'moment';

/** Какой край диапазона сейчас правится панелью минут */
export type TRangeEdge = 'start' | 'end';

export type TDateRangeSelection = {
    start: moment.Moment | null;
    end: moment.Moment | null;
    /** Край, к которому относится панель минут; `null` — панель скрыта */
    activeEdge: TRangeEdge | null;
};

const toMoment = (value?: string | Date | null) => (value ? moment(value) : null);

/**
 * Выбор диапазона тапами по часовым ячейкам: первый тап — начало, второй —
 * конец. Тап раньше начала переносит начало, тап по готовому диапазону
 * начинает выбор заново. Минуты ячейки — «00», пока их не сменили в панели.
 */
export const useDateRangeSelection = (
    initialStart?: string | Date | null,
    initialEnd?: string | Date | null
) => {
    // Исходная запись: по ней календарь выбирает первую неделю и расширяет часы
    const [initialSelection] = useState<TDateRangeSelection>(() => ({
        start: toMoment(initialStart),
        end: toMoment(initialEnd),
        activeEdge: null,
    }));
    const [selection, setSelection] = useState<TDateRangeSelection>(initialSelection);

    const pickCell = (cell: moment.Moment) =>
        setSelection(({ start, end }) => {
            const isNewRange = !start || end || cell.isBefore(start, 'hour');
            if (isNewRange) return { start: cell.clone(), end: null, activeEdge: 'start' };

            return { start, end: cell.clone(), activeEdge: 'end' };
        });

    const setMinutes = (minutes: number) =>
        setSelection((prev) => {
            if (!prev.activeEdge) return prev;
            const edge = prev[prev.activeEdge];
            if (!edge) return prev;

            return { ...prev, [prev.activeEdge]: edge.clone().minutes(minutes) };
        });

    /** Край, заданный точным временем снаружи (не тапом), — например из ручного выбора */
    const setEdge = (edge: TRangeEdge, value: moment.Moment) =>
        setSelection((prev) => ({ ...prev, [edge]: value.clone(), activeEdge: edge }));

    const isComplete = Boolean(selection.start && selection.end);
    const isValid = Boolean(selection.start && selection.end?.isAfter(selection.start));

    return { selection, initialSelection, pickCell, setMinutes, setEdge, isComplete, isValid };
};

/** Состояние выбора и действия над ним — отдаётся календарю тем, кто его встраивает */
export type TDateRangeSelectionApi = ReturnType<typeof useDateRangeSelection>;
